import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SLOW_SCROLL_STEP_PX = 1.2;
const SLOW_SCROLL_INTERVAL_MS = 5;

function SelectSlowScrollButton({
  direction,
  viewportRef,
}: {
  direction: 'up' | 'down';
  viewportRef: React.RefObject<HTMLDivElement>;
}) {
  const intervalRef = React.useRef<number | null>(null);

  const stopScrolling = React.useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const scrollOnce = React.useCallback(() => {
    viewportRef.current?.scrollBy({
      top: direction === 'up' ? -SLOW_SCROLL_STEP_PX : SLOW_SCROLL_STEP_PX,
    });
  }, [direction, viewportRef]);

  const startScrolling = React.useCallback(() => {
    if (intervalRef.current !== null) return;
    scrollOnce();
    intervalRef.current = window.setInterval(scrollOnce, SLOW_SCROLL_INTERVAL_MS);
  }, [scrollOnce]);

  React.useEffect(() => stopScrolling, [stopScrolling]);

  const Icon = direction === 'up' ? ChevronUp : ChevronDown;

  return (
    <div
      className="flex items-center justify-center py-1"
      onPointerEnter={startScrolling}
      onPointerLeave={stopScrolling}
      onPointerDown={(event) => event.preventDefault()}
    >
      <Icon className="h-4 w-4" />
    </div>
  );
}

interface SelectContentProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  scrollButtonBehavior?: 'default' | 'slow';
}

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = 'popper', scrollButtonBehavior = 'default', ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const hasSlowScrollButtons = scrollButtonBehavior === 'slow';

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'z-[3000] min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover text-foreground shadow-md animate-fade-in',
          className
        )}
        position={position}
        {...props}
      >
        {hasSlowScrollButtons ? (
          <SelectSlowScrollButton direction="up" viewportRef={viewportRef} />
        ) : (
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1">
            <ChevronUp className="h-4 w-4" />
          </SelectPrimitive.ScrollUpButton>
        )}
        <SelectPrimitive.Viewport ref={viewportRef} className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        {hasSlowScrollButtons ? (
          <SelectSlowScrollButton direction="down" viewportRef={viewportRef} />
        ) : (
          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1">
            <ChevronDown className="h-4 w-4" />
          </SelectPrimitive.ScrollDownButton>
        )}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-muted hover:text-foreground',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;


